import { Link } from 'react-router-dom';
import { Rating } from '@/components/ui/Rating';
import { formatAmount } from '@/lib/money';
import { ROUTES } from '@/config/navigation';
import { FUEL_LABELS, TRANSMISSION_LABELS } from '@/config/platform';
import type { SearchResultItem } from '@/lib/supabase/database.types';

/**
 * Carte véhicule — accueil et résultats de recherche.
 *
 * La maquette resserre la carte : photo en 4/3, badge en surimpression,
 * caractéristiques réunies sur une seule ligne grise, et en bas le prix face au
 * bouton. La densité d'information est la même qu'avant, mais l'œil n'a plus
 * que trois arrêts au lieu de six.
 */
export function VehicleCard({ vehicle, search }: { vehicle: SearchResultItem; search?: string }) {
  const to = `${ROUTES.vehicle(vehicle.id)}${search ? `?${search}` : ''}`;

  const specs = [
    vehicle.year,
    vehicle.city.name,
    TRANSMISSION_LABELS[vehicle.transmission],
    `${vehicle.seats} places`,
    FUEL_LABELS[vehicle.fuel],
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-card transition-shadow hover:shadow-lift">
      <Link to={to} className="relative block aspect-[4/3] overflow-hidden bg-ink-100">
        {vehicle.photo ? (
          <img
            src={vehicle.photo}
            alt={`${vehicle.brand} ${vehicle.model}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-ink-400">
            Photo à venir
          </div>
        )}

        {/* Une seule pastille en surimpression : la note si elle existe, sinon
            « Nouveau ». Deux badges superposés se neutralisent. */}
        {vehicle.rating_count > 0 ? (
          <span className="absolute left-2.5 top-2.5 rounded-full bg-white/95 px-2.5 py-1 shadow-card">
            <Rating value={vehicle.rating_avg} count={vehicle.rating_count} />
          </span>
        ) : (
          <span className="absolute left-2.5 top-2.5 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-bold text-brand-700">
            Nouveau
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-[15px] font-bold leading-snug text-ink-950">
          <Link to={to} className="hover:underline">
            {vehicle.brand} {vehicle.model}
          </Link>
        </h3>
        <p className="mt-1 text-xs text-ink-500">{specs}</p>

        <p className="mt-2 text-xs text-ink-700">
          {vehicle.partner.display_name}
          {vehicle.partner.status === 'verified' && (
            <span className="ml-1.5 font-semibold text-emerald-800">✓ Vérifiée</span>
          )}
        </p>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          <div className="min-w-0">
            <p className="whitespace-nowrap text-base font-extrabold text-ink-950">
              {formatAmount(vehicle.daily_price, vehicle.currency)}
              <span className="text-xs font-medium text-ink-500"> / jour</span>
            </p>
            {vehicle.days > 1 && (
              <p className="text-xs text-ink-500">
                {formatAmount(vehicle.total_price, vehicle.currency)} · {vehicle.days} jours
              </p>
            )}
          </div>
          <Link
            to={to}
            className="shrink-0 rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-bold text-white hover:bg-brand-600"
          >
            Voir
          </Link>
        </div>
      </div>
    </article>
  );
}

export function VehicleCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="aspect-[4/3] animate-pulse bg-ink-100" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-2/3 animate-pulse rounded bg-ink-100" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-ink-100" />
        <div className="h-8 w-full animate-pulse rounded bg-ink-100" />
      </div>
    </div>
  );
}
